import { documentListWidget } from "sanity-plugin-dashboard-widget-document-list";
import { sscCruiseCodeSearchWidget } from "../plugins/ssc-cruise-code-search";
import { sscShopApiLinksWidget } from "../plugins/ssc-shop-api-links";
import { sscValidationChecksWidget } from "../plugins/ssc-validation-checks";
import { DashboardPluginConfig } from "@sanity/dashboard";
import { LiaShipSolid } from "react-icons/lia";
import { documentListExporter } from "../plugins/document-list-exporter";
import { landProgrammeExporter } from "../plugins/land-programme-exporter";
import { LandProgrammeExportRow } from "../plugins/land-programme-exporter/LandProgrammeExporter";
import { withFixedHeight } from "../plugins/commons/withFixedHeight";

type MissingAssetRow = {
  _id?: string;
  code: string;
  name: string;
  hasImage: boolean;
  hasDescription: boolean;
};

const mapMissingAssetReason = (data: MissingAssetRow) => {
  if (!data.name || data.name.length === 0) {
    return { ...data, reason: "missing name" };
  } else if (!data.hasImage) {
    return { ...data, reason: "missing image" };
  } else if (!data.hasDescription) {
    return { ...data, reason: "missing description" };
  }
  return { ...data, reason: "unknown" };
};

type LandProgrammeRef = {
  id: string;
  code: string;
  duration: number | null;
  descriptionHtml: string | null;
};

type CruiseDataForLandExport = {
  cruiseCode: string;
  departureDate: string;
  preLandProgrammesRefs: LandProgrammeRef[];
  postLandProgrammesRefs: LandProgrammeRef[];
};

const mapCruiseDataToLandProgrammeRows = (
  data: CruiseDataForLandExport
): LandProgrammeExportRow[] => {
  const toRow = (
    item: LandProgrammeRef,
    prePost: "PRE" | "PST"
  ): LandProgrammeExportRow => ({
    voyage_cod: data.cruiseCode,
    sail_date: data.departureDate,
    land_cod: item.code,
    pre_post: prePost,
    duration: item.duration,
    description: item.descriptionHtml
  });
  return [
    ...(data.preLandProgrammesRefs ?? []).map((item) => toRow(item, "PRE")),
    ...(data.postLandProgrammesRefs ?? []).map((item) => toRow(item, "PST"))
  ];
};

const landProgrammeExportQuery = `*[
  _type == "cruiseData"
  && !(_id in path("drafts.**"))
  && defined(cruiseCode)
] {
  cruiseCode,
  departureDate,
  "preLandProgrammesRefs": preLandProgrammes[].landProgramme->{ "id": _id, code, duration, descriptionHtml },
  "postLandProgrammesRefs": postLandProgrammes[].landProgramme->{ "id": _id, code, duration, descriptionHtml }
}`;

export default {
  name: "cruises-diagnostic",
  title: "Cruises Diagnostics",
  icon: LiaShipSolid,
  widgets: [
    sscCruiseCodeSearchWidget({
      layout: {
        width: "full"
      },
      silverseaApiRootUrl: process.env.SANITY_STUDIO_SILVERSEA_API
    }),
    withFixedHeight(
      sscShopApiLinksWidget({
        layout: {
          width: "medium"
        }
      })
    ),
    withFixedHeight(
      sscValidationChecksWidget({
        layout: {
          width: "medium"
        },
        silverseaApiRootUrl: process.env.SANITY_STUDIO_SILVERSEA_API
      })
    ),
    withFixedHeight(
      documentListWidget({
        title: "Unpublished/Slug-less Cruises with Visibility True",
        query:
          '*[_type == "cruise" && (_id in path("drafts.**") || !defined(metadata.routing.slug.en))  &&  data->isVisible == true] | order(_updatedAt desc)',
        limit: 1000,
        layout: {
          width: "medium"
        }
      })
    ),
    withFixedHeight(
      documentListWidget({
        title: "Cruises with map override",
        query:
          '*[_type == "cruise" && defined(mapOverride.asset) && defined(data) && data->cruiseGroup != "AntarcticaBridge"]',
        limit: 1000,
        layout: {
          width: "medium"
        }
      })
    ),
    withFixedHeight(
      documentListExporter<MissingAssetRow>({
        title: "Published Land Programmes without name or image",
        query: `*[
                _type == "landProgramme"
                && !(_id in path("drafts.**"))
                && (length(name) == 0 || !defined(image) || length(descriptionHtml) == 0)
                && (
                  _id in *[_type == "cruiseData"].preLandProgrammes[].landProgramme._ref
                  || _id in *[_type == "cruiseData"].postLandProgrammes[].landProgramme._ref
                )
              ] { code, name, "hasImage": defined(image), "hasDescription": length(descriptionHtml) > 0 }`,
        mapResult: mapMissingAssetReason,
        layout: {
          width: "medium"
        }
      })
    ),
    withFixedHeight(
      documentListExporter<MissingAssetRow>({
        title: "Published Hotels without name or image",
        query: `*[
                _type == "hotel"
                && !(_id in path("drafts.**"))
                && (length(name) == 0 || !defined(image) || length(descriptionHtml) == 0)
                && (
                  _id in *[_type == "cruiseData"].preHotel[]._ref
                  || _id in *[_type == "cruiseData"].postHotel[]._ref
                )
              ] { code, name, "hasImage": defined(image), "hasDescription": length(descriptionHtml) > 0  }`,
        mapResult: mapMissingAssetReason,
        layout: {
          width: "medium"
        }
      })
    ),
    withFixedHeight(
      documentListExporter<MissingAssetRow>({
        title: "Published Shorexes without name or image",
        query: `*[
  _type == "shorex"
  && !(_id in path("drafts.**"))
  && (length(name) == 0 || !defined(image) || length(descriptionHtml) == 0)
] { _id, code, name, "hasImage": defined(image), "hasDescription": length(descriptionHtml) > 0  }`,
        referencedIdsQuery: `*[_type == "cruiseData" && !(_id in path("drafts.**"))].itinerary[].confirmedShorexes[].shorex._ref`,
        mapResult: mapMissingAssetReason,
        layout: {
          width: "medium"
        }
      })
    ),
    withFixedHeight(
      landProgrammeExporter<CruiseDataForLandExport>({
        title: "Land Programmes & City Stays Export",
        query: landProgrammeExportQuery,
        mapResult: mapCruiseDataToLandProgrammeRows,
        layout: {
          width: "medium"
        }
      })
    )
  ]
} satisfies DashboardPluginConfig;
